// src/knowledge/KnowledgePage.tsx
import * as React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import KnowledgeIcon from "./KnowledgeIcon";
import AddKnowledgeDialog from "./AddKnowledgeDialog";
import FolderDialog from "./FolderDialog";
import FolderSidebar, { KNOWLEDGE_CARD, DragItem } from "./FolderSidebar";
import MoveToFolderDialog from "./MoveToFolderDialog";
import {
  AddUserKnowledgeInput,
  CreateKnowledgeFolderInput,
  DeleteKnowledgeFolderInput,
  DeleteUserKnowledgeInput,
  GetKnowledgeFoldersInput,
  GetUserKnowledgeInput,
  KnowledgeFolder,
  RenameKnowledgeFolderInput,
  User,
  UserKnowledge,
} from "../../models/API";
import { useEffect, useState } from "react";
import { getUserKnowledge, getKnowledgeFolders } from "../../graphql/queries";
import {
  addUserKnowledge,
  createKnowledgeFolder,
  deleteKnowledgeFolder,
  deleteUserKnowledge,
  renameKnowledgeFolder,
  syncKnowledge,
} from "../../graphql/mutations";
import { generateAuditedClient } from "../../audit/auditedClient";
import ErrorModal from "../ErrorModal";
import Button from "@mui/material/Button";
import { covertUtcToLocal } from "../utils/formatters";
import {
  parseKnowledgeAttributes,
  serializeKnowledgeAttributes,
} from "../utils/knowledgeAttributes";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmationDialog from "../utils/ConfirmationDialog";
import { useGlobal } from "../../context/GlobalContext";
import { DndProvider, useDrag } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

interface KnowledgePageProps {
  currentUserId: number;
  currentUserName: string;
  userConfig: User;
  effectiveRole: string;
}

function DraggableKnowledgeCard({
  row,
  children,
}: {
  row: UserKnowledge;
  children: React.ReactNode;
}) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: KNOWLEDGE_CARD,
      item: { id: row.id, folder_id: row.folder_id ?? null } as DragItem,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [row.id, row.folder_id]
  );

  return (
    <Box
      ref={drag}
      sx={{
        opacity: isDragging ? 0.6 : 1,
        transform: isDragging ? "scale(0.95)" : "scale(1)",
        transition:
          "transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease",
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.15)" : "none",
        borderRadius: isDragging ? 2 : 0,
        cursor: "grab",
      }}
    >
      {children}
    </Box>
  );
}

function KnowledgePageContent({
  currentUserId,
  currentUserName,
  userConfig,
  effectiveRole,
}: KnowledgePageProps) {
  const [rows, setRows] = React.useState<UserKnowledge[]>([]);
  const [openAdd, setOpenAdd] = React.useState(false);
  const [editRow, setEditRow] = React.useState<UserKnowledge | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [, setUserKnowledgeData] = useState<UserKnowledge[]>([]);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [error, setError] = useState<any>(null);
  const client = generateAuditedClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const { globalTeamId } = useGlobal();

  // Folder state
  const [folders, setFolders] = useState<KnowledgeFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<
    number | null | "all"
  >("all");
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderDialogMode, setFolderDialogMode] = useState<"create" | "rename">(
    "create"
  );
  const [renamingFolder, setRenamingFolder] = useState<KnowledgeFolder | null>(
    null
  );
  const [folderError, setFolderError] = useState<string | null>(null);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [deleteFolderId, setDeleteFolderId] = useState<number | null>(null);

  // Move to folder dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveItemId, setMoveItemId] = useState<number | null>(null);
  const [moveItemCurrentFolderId, setMoveItemCurrentFolderId] = useState<
    number | null
  >(null);

  async function fetchFolders() {
    try {
      const input: GetKnowledgeFoldersInput = {
        team_id: globalTeamId,
        user_id: currentUserId,
      };
      const result = await client.graphql({
        query: getKnowledgeFolders,
        variables: { input },
      });
      // @ts-ignore
      const folderList: KnowledgeFolder[] =
        result.data.getKnowledgeFolders || [];
      setFolders(folderList);
    } catch (err) {
      console.error("Failed to fetch folders:", err);
    }
  }

  async function fetchUserKnowledge() {
    try {
      const getUserKnowledgeInput: GetUserKnowledgeInput = {
        user_id: currentUserId,
        team_id: globalTeamId,
      };

      // Apply folder filtering
      if (selectedFolderId === "all") {
        // "All Items" shows root-level items (no folder) — folders in sidebar navigate to folder contents
        getUserKnowledgeInput.filter_root = true;
      } else if (selectedFolderId === null) {
        getUserKnowledgeInput.filter_root = true;
      } else {
        getUserKnowledgeInput.folder_id = selectedFolderId;
      }

      const dataSourcesData = await client.graphql({
        query: getUserKnowledge,
        variables: {
          input: getUserKnowledgeInput,
        },
      });
      // @ts-ignore
      const userKnowledge: UserKnowledge[] =
        dataSourcesData.data.getUserKnowledge;
      setUserKnowledgeData(userKnowledge);
      setRows(userKnowledge);
      console.log("fetch userKnowledge() refreshed");
    } catch (err) {
      console.error(err);
      setError(err);
    }
  }

  useEffect(() => {
    console.log("User: [" + currentUserId + "] knowledge is refreshing...");
    fetchUserKnowledge();
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag, globalTeamId, selectedFolderId]);

  useEffect(() => {
    if (rows.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get("id");
    if (idParam) {
      const item = rows.find((r) => r.id === parseInt(idParam, 10));
      if (item) setEditRow(item);
    }
  }, [rows]);

  const onRefresh = async () => {
    setLoading(true);
    setRefreshFlag(!refreshFlag);
    setLoading(false);
  };

  const onDelete = async (id: number) => {
    setIsDialogOpen(true);
    setDeleteId(id);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    try {
      const deleteUserKnowledgeInput: DeleteUserKnowledgeInput = {
        id: deleteId,
      };
      await client.graphql({
        query: deleteUserKnowledge,
        variables: {
          input: deleteUserKnowledgeInput,
        },
      });
      // Trigger Bedrock KB sync asynchronously
      try {
        await client.graphql({
          query: syncKnowledge,
          variables: {
            input: {
              knowledge_id: deleteId,
              user_id: currentUserId,
              team_id: globalTeamId,
              action: "delete",
            },
          },
        });
      } catch (syncErr) {
        console.warn("Knowledge sync trigger failed (non-blocking):", syncErr);
      }
      setRefreshFlag(!refreshFlag);
      console.log("fetchUserKnowledge() refreshed");
    } catch (err) {
      console.error(err);
      setError(err);
    }
  };

  // Folder CRUD handlers
  const handleCreateFolder = async (
    name: string,
    isPublic: boolean,
    isGlobal: boolean
  ) => {
    try {
      const input: CreateKnowledgeFolderInput = {
        folder_name: name,
        team_id: globalTeamId,
        is_public: isPublic,
        is_global: isGlobal,
        creator_id: currentUserId,
      };
      await client.graphql({
        query: createKnowledgeFolder,
        variables: { input },
      });
      setFolderDialogOpen(false);
      setFolderError(null);
      fetchFolders();
    } catch (err: any) {
      const message = err?.errors?.[0]?.message || "Failed to create folder";
      setFolderError(message);
    }
  };

  const handleRenameFolder = async (
    name: string,
    _isPublic: boolean,
    _isGlobal: boolean
  ) => {
    if (!renamingFolder) return;
    try {
      const input: RenameKnowledgeFolderInput = {
        id: renamingFolder.id,
        folder_name: name,
        updated_by: currentUserId,
      };
      await client.graphql({
        query: renameKnowledgeFolder,
        variables: { input },
      });
      setFolderDialogOpen(false);
      setFolderError(null);
      setRenamingFolder(null);
      fetchFolders();
    } catch (err: any) {
      const message = err?.errors?.[0]?.message || "Failed to rename folder";
      setFolderError(message);
    }
  };

  const handleDeleteFolder = async () => {
    if (deleteFolderId === null) return;
    setDeleteFolderDialogOpen(false);
    try {
      const input: DeleteKnowledgeFolderInput = { id: deleteFolderId };
      await client.graphql({
        query: deleteKnowledgeFolder,
        variables: { input },
      });
      setDeleteFolderId(null);
      // If we were viewing the deleted folder, go back to all
      if (selectedFolderId === deleteFolderId) {
        setSelectedFolderId("all");
      }
      fetchFolders();
      setRefreshFlag(!refreshFlag);
    } catch (err) {
      console.error(err);
      setError(err);
    }
  };

  // Move item to folder (via drag-and-drop or dialog)
  const handleMoveItem = async (
    itemId: number,
    targetFolderId: number | null
  ) => {
    const item = rows.find((r) => r.id === itemId);
    if (!item) return;

    try {
      const input: AddUserKnowledgeInput = {
        id: item.id,
        team_id: item.is_global ? 1 : globalTeamId,
        knowledge_name: item.knowledge_name,
        description: item.description,
        knowledge_type: item.knowledge_type,
        content: item.content,
        // Re-serialized once: writing the fetched value back verbatim used to
        // wrap it in another JSON-string layer on every move.
        attributes: serializeKnowledgeAttributes(item.attributes),
        is_public: item.is_public,
        user_id: currentUserId,
        folder_id: targetFolderId,
      };
      await client.graphql({
        query: addUserKnowledge,
        variables: { input },
      });
      setMoveDialogOpen(false);
      setMoveItemId(null);
      fetchFolders();
      setRefreshFlag(!refreshFlag);
    } catch (err) {
      console.error(err);
      setError(err);
    }
  };

  const contentPreview = (text: string) =>
    text.length > 120 ? text.slice(0, 117) + "…" : text;

  // Helper function to determine if delete should be disabled
  const isDeleteDisabled = (row: UserKnowledge): boolean => {
    const isAdmin = effectiveRole === "admin";
    const isOperator = effectiveRole === "operator";

    if (row.is_global) {
      return !((isAdmin || isOperator) && globalTeamId === 1);
    }
    if (row.is_public) {
      return !(isAdmin || isOperator);
    }
    return false;
  };

  // Helper function to get card background color and border color
  const getCardStyle = (row: UserKnowledge) => {
    if (row.knowledge_type === "skill") {
      return {
        backgroundColor: "rgba(156, 39, 176, 0.03)",
        borderColor: "#9c27b0",
      };
    } else if (row.is_global) {
      return {
        backgroundColor: "rgba(255, 152, 0, 0.03)",
        borderColor: "#ff9800",
      };
    } else if (row.is_public) {
      return {
        backgroundColor: "rgba(46, 125, 50, 0.03)",
        borderColor: "#2e7d32",
      };
    } else {
      return {
        backgroundColor: "rgba(21, 101, 192, 0.03)",
        borderColor: "#1565c0",
      };
    }
  };

  const getFolderName = (
    folderId: number | null | undefined
  ): string | null => {
    if (!folderId) return null;
    const folder = folders.find((f) => f.id === folderId);
    return folder ? folder.folder_name : null;
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0, p: 2, pb: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={700}>
            Knowledge
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={onRefresh}
                  disabled={loading}
                  aria-label="refresh"
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add knowledge
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Main Content with Sidebar */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          overflow: "hidden",
        }}
      >
        {/* Folder Sidebar */}
        <FolderSidebar
          folders={folders}
          selectedFolderId={selectedFolderId}
          onSelectFolder={setSelectedFolderId}
          onCreateFolder={() => {
            setFolderDialogMode("create");
            setRenamingFolder(null);
            setFolderError(null);
            setFolderDialogOpen(true);
          }}
          onRenameFolder={(folder) => {
            setFolderDialogMode("rename");
            setRenamingFolder(folder);
            setFolderError(null);
            setFolderDialogOpen(true);
          }}
          onDeleteFolder={(folderId) => {
            setDeleteFolderId(folderId);
            setDeleteFolderDialogOpen(true);
          }}
          onMoveItem={handleMoveItem}
          effectiveRole={effectiveRole}
          currentUserId={currentUserId}
          globalTeamId={globalTeamId}
        />

        {/* Scrollable Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            p: 2,
            pt: 1,
          }}
        >
          {rows.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                px: 4,
                textAlign: "center",
              }}
            >
              <InboxOutlinedIcon
                sx={{ fontSize: 64, color: "grey.300", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                No items here yet
              </Typography>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ mt: 0.5, maxWidth: 320 }}
              >
                {selectedFolderId === "all"
                  ? "Add knowledge items or create folders to organize your content."
                  : "Drag items here or use the move button to organize your knowledge."}
              </Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            {rows.map((row) => {
              const cardStyle = getCardStyle(row);
              const folderName = getFolderName(row.folder_id);
              return (
                <Grid item xs={12} sm={6} md={4} key={row.id}>
                  <DraggableKnowledgeCard row={row}>
                    <Card
                      sx={{
                        backgroundColor: cardStyle.backgroundColor,
                        borderLeft: `6px solid ${cardStyle.borderColor}`,
                        borderRadius: 2,
                        transition: "all 0.25s ease",
                        "&:hover": {
                          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => {
                          if (row.is_global && globalTeamId !== 1) return;
                          const params = new URLSearchParams(
                            window.location.search
                          );
                          params.set("id", String(row.id));
                          window.history.replaceState(
                            {},
                            "",
                            `?${params.toString()}`
                          );
                          setEditRow(row);
                        }}
                        sx={
                          row.is_global && globalTeamId !== 1
                            ? { cursor: "default" }
                            : {}
                        }
                      >
                        <CardHeader
                          avatar={
                            <KnowledgeIcon
                              type={row.knowledge_type}
                              fontSize="medium"
                            />
                          }
                          title={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                noWrap
                              >
                                {row.knowledge_name} (ID: {row.id})
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  row.knowledge_type === "skill"
                                    ? "SKILL"
                                    : row.knowledge_type
                                }
                                variant={
                                  row.knowledge_type === "skill"
                                    ? "filled"
                                    : "outlined"
                                }
                                color={
                                  row.knowledge_type === "skill"
                                    ? "secondary"
                                    : "default"
                                }
                              />
                              {row.knowledge_type === "skill" &&
                                (() => {
                                  const attrs = parseKnowledgeAttributes(
                                    row.attributes
                                  );
                                  return attrs.version ? (
                                    <Chip
                                      size="small"
                                      label={`v${attrs.version}`}
                                      variant="outlined"
                                      color="info"
                                    />
                                  ) : null;
                                })()}
                            </Stack>
                          }
                          subheader={
                            <Stack spacing={0.5}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {row.description}
                              </Typography>
                              {row.knowledge_type === "skill" &&
                                (() => {
                                  const attrs = parseKnowledgeAttributes(
                                    row.attributes
                                  );
                                  const tags = Array.isArray(attrs.tags)
                                    ? (attrs.tags as string[])
                                    : [];
                                  return tags.length > 0 ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      flexWrap="wrap"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {tags.map((tag: string) => (
                                        <Chip
                                          key={tag}
                                          size="small"
                                          label={tag}
                                          variant="outlined"
                                          sx={{ fontSize: "0.7rem" }}
                                        />
                                      ))}
                                    </Stack>
                                  ) : null;
                                })()}
                            </Stack>
                          }
                        />
                        <CardContent>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {contentPreview(row.content)}
                            </Typography>
                            {folderName && (
                              <Chip
                                size="small"
                                label={folderName}
                                icon={
                                  <FolderOutlinedIcon sx={{ fontSize: 16 }} />
                                }
                                variant="outlined"
                                color="default"
                                sx={{
                                  alignSelf: "flex-start",
                                  borderRadius: "8px",
                                  "& .MuiChip-icon": {
                                    color: "text.secondary",
                                  },
                                }}
                              />
                            )}
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.creator +
                                  " created at " +
                                  covertUtcToLocal(row.created_at) +
                                  ". " +
                                  row.last_updated_by +
                                  " updated at " +
                                  covertUtcToLocal(row.updated_at)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        sx={{ px: 1, pb: 1 }}
                      >
                        <Tooltip title="Move to Folder">
                          <IconButton
                            onClick={() => {
                              setMoveItemId(row.id);
                              setMoveItemCurrentFolderId(row.folder_id ?? null);
                              setMoveDialogOpen(true);
                            }}
                            aria-label="move to folder"
                            disabled={isDeleteDisabled(row)}
                          >
                            <DriveFileMoveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => onDelete(row.id)}
                            aria-label="delete user knowledge"
                            disabled={isDeleteDisabled(row)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Card>
                  </DraggableKnowledgeCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>

      {/* Delete Knowledge Confirmation */}
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onConfirm={handleConfirm}
        onClose={handleClose}
        message="Are you sure you want to delete this knowledge?"
      />

      {/* Delete Folder Confirmation */}
      <ConfirmationDialog
        isOpen={deleteFolderDialogOpen}
        onConfirm={handleDeleteFolder}
        onClose={() => {
          setDeleteFolderDialogOpen(false);
          setDeleteFolderId(null);
        }}
        message="Are you sure you want to delete this folder? Items in this folder will be moved to root level."
      />

      {/* Folder Create/Rename Dialog */}
      <FolderDialog
        open={folderDialogOpen}
        mode={folderDialogMode}
        initialName={renamingFolder?.folder_name || ""}
        onClose={() => {
          setFolderDialogOpen(false);
          setFolderError(null);
          setRenamingFolder(null);
        }}
        onSubmit={
          folderDialogMode === "create"
            ? handleCreateFolder
            : handleRenameFolder
        }
        effectiveRole={effectiveRole}
        globalTeamId={globalTeamId}
        error={folderError}
      />

      {/* Move to Folder Dialog */}
      <MoveToFolderDialog
        open={moveDialogOpen}
        folders={folders}
        currentFolderId={moveItemCurrentFolderId}
        onClose={() => {
          setMoveDialogOpen(false);
          setMoveItemId(null);
        }}
        onMove={(folderId) => {
          if (moveItemId !== null) {
            handleMoveItem(moveItemId, folderId);
          }
        }}
      />

      <ErrorModal error={error} onClose={() => setError(null)} />
      {/* Add */}
      <AddKnowledgeDialog
        open={openAdd}
        mode="add"
        currentUserId={currentUserId}
        currentTeamId={globalTeamId}
        userConfig={userConfig}
        effectiveRole={effectiveRole}
        folderId={
          typeof selectedFolderId === "number" ? selectedFolderId : null
        }
        onClose={() => {
          setOpenAdd(false);
          onRefresh();
        }}
      />

      {/* Edit */}
      <AddKnowledgeDialog
        open={!!editRow}
        mode="edit"
        currentUserId={currentUserId}
        currentTeamId={globalTeamId}
        initial={editRow ?? undefined}
        userConfig={userConfig}
        effectiveRole={effectiveRole}
        onClose={() => {
          setEditRow(null);
          const params = new URLSearchParams(window.location.search);
          params.delete("id");
          window.history.replaceState({}, "", `?${params.toString()}`);
          onRefresh();
        }}
      />
    </Box>
  );
}

export default function KnowledgePage(props: KnowledgePageProps) {
  return (
    <DndProvider backend={HTML5Backend}>
      <KnowledgePageContent {...props} />
    </DndProvider>
  );
}
